<script setup lang="ts">
import { computed, watch } from "vue";
import { vIntersectionObserver } from "@vueuse/components";
import { useToggle, useWindowSize } from "@vueuse/core";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import VModal from "@magnit/core/src/components/VModal/VModal.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import { scrollLock } from "@magnit/core/src/helpers/scrollLock";
import VDropdown from "~/components/VDropdown.vue";
import VProductShareContent from "~/containers/VProductShare/VProductShareContent.vue";
import { MODALS, useModalsStore } from "~/store/modals";

interface IProductShareProps {
  id: string;
  name?: string;
  categoryKey: string;
  discountCategory?: string;
}

defineProps<IProductShareProps>();

const modalsStore = useModalsStore();
const [, dropdownToggle] = useToggle();
const { width: windowWidth } = useWindowSize();
const { send } = useAnalytics();

const onVisibilityShare = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    send("CardDetail:Share:View");
  }
};

const onShareClick = () => {
  modalsStore.open(MODALS.ProductShare);
  send("CardDetail:Share:Click");
};

const isActive = computed(() => {
  return modalsStore.getActive() === MODALS.ProductShare;
});

const isOpen = computed(() => {
  return modalsStore.isOpen(MODALS.ProductShare);
});

const checkVisibility = () => {
  if (!isActive.value) {
    return;
  }

  if (window.matchMedia("(min-width: 560px)").matches) {
    scrollLock.disable();
  } else {
    scrollLock.enable();
  }
};

const onOutsideClick = () => {
  dropdownToggle(false);
  modalsStore.close(MODALS.ProductShare);
};

watch(windowWidth, () => {
  checkVisibility();
});
</script>

<template>
  <div>
    <VInlineButton
      v-intersection-observer="onVisibilityShare"
      @click="onShareClick"
    >
      <template #icon>
        <VIcon name="24-outline-share" />
      </template>
    </VInlineButton>
    <div class="product-share">
      <VModal
        :id="MODALS.ProductShare"
        :active="isActive"
        :open="isOpen"
        :type="{ xs: 'bottom-sheet', m: 'modal' }"
        title="Поделиться"
        wrapper-class="product-share__modal"
        @close="modalsStore.close(MODALS.ProductShare)"
      >
        <VProductShareContent
          :id="id"
          :name="name"
          :discount-category="discountCategory"
          :category-key="categoryKey"
        />
      </VModal>

      <VDropdown
        class="product-share__dropdown"
        :active="isOpen"
        :outside-ignore="['.product-share__modal']"
        position="right"
        @outside-click="onOutsideClick"
      >
        <div class="product-share__dropdown-content">
          <VProductShareContent
            :id="id"
            :name="name"
            :discount-category="discountCategory"
            :category-key="categoryKey"
          />
        </div>
        <span class="product-share__dropdown-arrow">
          <svg
            width="20"
            height="8"
            viewBox="0 0 20 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 0C13 0 15.9999 8 20 8H0C3.9749 8 7 0 10 0Z"
              fill="white"
            />
          </svg>
        </span>
      </VDropdown>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.product-share {
  &__dropdown {
    display: none;
    z-index: var(--pl-z-index-level2);
    position: relative;
  }

  &__dropdown-content {
    padding: 12px;
    width: 286px;
    background-color: var(--pl-surface-primary-default);
    box-shadow: var(--pl-shadow-l);
    border-radius: 16px;
  }

  &__dropdown-arrow {
    position: absolute;
    top: -17px;
    right: 15px;
    width: 20px;
    height: 8px;
  }
}

:global(.product-share__modal) {
  @media (--pl-viewport-from-m) {
    display: none !important;
  }
}

@media (--pl-viewport-from-m) {
  .product-share {
    &__dropdown {
      display: block;
    }
  }
}
</style>
